/**
 * 质量事故 AI 提取确认弹窗 + 详情弹窗
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import type { ExtractionResult, QualityIncidentDetail } from '@/types/api';

interface Props {
  showExtractionModal: boolean;
  showDetailModal: boolean;
  extractionData: {
    incident_id: number;
    merge_result?: ExtractionResult | null;
    file_results: ExtractionResult[];
  } | null;
  detailData: QualityIncidentDetail | null;
  onCloseExtraction: () => void;
  onCloseDetail: () => void;
  onConfirm: () => Promise<void>;
  onRepair?: (incidentId: number) => Promise<void>;
}

const SECTIONS: Array<{key:string; label:string; fields:string[]; enumFields:Record<string,string[]>}> = [
  {key:'basic_info', label:'基础信息', fields:['incident_type','severity_level','discovery_stage','discovery_time','occurrence_time','closure_time','recorder_name','reviewer_name','source_report_path'],
    enumFields: {'incident_type':['制程不良','来料不良','客诉不良','仓储锈蚀','实验异常'], 'severity_level':['一般','重大','特重大'], 'discovery_stage':['客户投诉','子公司投诉','生产发现']}},
  {key:'product_info', label:'产品信息', fields:['customer_name','product_name','product_type','spec_model','product_batch','order_no','total_quantity'], enumFields:{}},
  {key:'incident_scene', label:'事故现场', fields:['workshop','production_line','shift','process_stage','production_batch'],
    enumFields: {'workshop':['印铁车间','制罐车间','制盖车间','塑胶车间'], 'shift':['早班','中班','晚班'], 'process_stage':['来料','印刷','涂布','裁剪','冲压','缩颈','卷封','焊缝','成品仓']}},
  {key:'defect_details', label:'缺陷详情', fields:['defect_location','defect_category','detection_method','defect_detail','total_output','defect_quantity','disposal_method','rework_quantity','scrap_quantity','impact_scope'],
    enumFields: {'defect_location':['罐身','罐盖','卷边','内壁','接缝'], 'defect_category':['漏罐','焊缝针孔','卷封不良','内涂漏涂','外漆刮花','罐体变形','瘪罐','罐盖卷边','生锈氧化','油污','爆罐','涂层脱落'], 'detection_method':['人工巡检','在线检漏机','压力测试','出货抽检','客户反馈'], 'disposal_method':['让步放行','选别使用','报废','返工','挪作它用','其它'], 'impact_scope':['在制半成品','本批次','同基材','已出货','客诉退回']}},
  {key:'losses', label:'造成损失', fields:['downtime_duration','total_loss','scrap_loss','rework_loss','customer_claim'], enumFields:{}},
  {key:'responsibility', label:'责任追溯', fields:['responsible_dept','direct_responsible','indirect_responsible','mgmt_responsible','other_responsible','cause_category'],
    enumFields: {'cause_category':['人','机','料','法','环','管']}},
  {key:'improvement', label:'改善措施', fields:['containment_action','improvement_project','improvement_category','planned_completion','actual_completion','main_dept','main_dept_head','related_depts','related_dept_heads','verify_dept','verifier','verify_evaluation'], enumFields:{}},
];

const FIELD_LABELS: Record<string,string> = {
  incident_type:'事故类型', severity_level:'事故等级', discovery_stage:'发现环节', discovery_time:'发现时间', occurrence_time:'发生时间', closure_time:'闭环时间', recorder_name:'录入人', reviewer_name:'审核人', source_report_path:'报告路径',
  customer_name:'客户名称', product_name:'产品名称', product_type:'产品类型', spec_model:'规格型号', product_batch:'产品批号', order_no:'订单号', total_quantity:'产品总数',
  workshop:'生产车间', production_line:'产线编号', shift:'班次', process_stage:'发生工序', production_batch:'生产批次号',
  defect_location:'缺陷位置', defect_category:'缺陷分类', detection_method:'检出方式', defect_detail:'缺陷描述', total_output:'总产量', defect_quantity:'不合格数量', disposal_method:'处置方式', rework_quantity:'返工数量', scrap_quantity:'报废数量', impact_scope:'影响范围',
  downtime_duration:'停机时长（小时）', total_loss:'总损失金额（元）', scrap_loss:'报废损失金额（元）', rework_loss:'返工损失金额（元）', customer_claim:'客户索赔金额（元）',
  responsible_dept:'责任部门', direct_responsible:'直接责任人', indirect_responsible:'间接责任人', mgmt_responsible:'管理责任人', other_responsible:'其他责任人', cause_category:'原因归类',
  containment_action:'遏制措施', improvement_project:'改进项目', improvement_category:'改进类别', planned_completion:'计划完成时间', actual_completion:'实际完成时间', main_dept:'主责部门', main_dept_head:'主责部门负责人', related_depts:'相关部门', related_dept_heads:'相关部门负责人', verify_dept:'验证部门', verifier:'验证人', verify_evaluation:'验证评价',
};

const SECTION_LABELS: Record<string,string> = {
  basic_info:'基础信息', product_info:'产品信息', incident_scene:'事故现场', defect_details:'缺陷详情', losses:'造成损失', responsibility:'责任追溯', improvement:'改善措施',
};

const modalOverlay = { display:'flex', alignItems:'center', justifyContent:'center', position:'fixed' as const, inset:0, background:'rgba(0,0,0,.3)', zIndex:2000, padding:24, backdropFilter:'blur(2px)' };
const modalLarge = { background:'white', borderRadius:18, width:'100%', maxWidth:900, maxHeight:'90vh', display:'flex', flexDirection:'column' as const, boxShadow:'0 24px 80px rgba(0,0,0,.12)', overflow:'hidden' };
const modalHeader = { display:'flex', alignItems:'center', gap:12, padding:'16px 20px', borderBottom:'1px solid #f0ebe4' };
const modalBody = { padding:'16px 20px', overflow:'auto', flex:1, display:'flex', flexDirection:'column' as const, gap:10 };
const modalFooter = { padding:'16px 24px', borderTop:'1px solid #f0ebe4', display:'flex', justifyContent:'flex-end', gap:8 };
const sectionStyle = { border:'1px solid #e5e7eb', borderRadius:8, overflow:'hidden' as const };
const summaryStyle = { padding:'10px 16px', fontSize:'.875rem', fontWeight:600, cursor:'pointer', background:'#f9fafb', display:'flex', justifyContent:'space-between' as const };
const gridStyle = { padding:'8px 16px 16px', display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:8 };
const fieldStyle = { display:'flex', flexDirection:'column' as const, gap:2 };
const labelStyle = { fontSize:'.6875rem', color:'#6b7280' };
const inputStyle = (conflict: boolean) => ({ border:`1px solid ${conflict ? '#ef4444' : '#d1d5db'}`, borderRadius:6, padding:'6px 8px', fontSize:'.8125rem', background: conflict ? '#fef2f2' : 'white' });

export default function QualityExtractionModal({
  showExtractionModal, showDetailModal, extractionData, detailData,
  onCloseExtraction, onCloseDetail, onConfirm,
}: Props) {
  const [editing, setEditing] = useState<Record<string, any>>({});
  const [confirming, setConfirming] = useState(false);

  // 初始化编辑数据
  useState(() => {
    if (extractionData?.merge_result?.merge_json) {
      setEditing(JSON.parse(JSON.stringify(extractionData.merge_result.merge_json)));
    }
  });

  const handleConfirm = async () => {
    setConfirming(true);
    try { await onConfirm(); }
    finally { setConfirming(false); }
  };

  if (!showExtractionModal && !showDetailModal) return null;

  return (
    <>
      {/* 提取确认弹窗 */}
      <AnimatePresence>
        {showExtractionModal && extractionData && (
          <motion.div style={modalOverlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={onCloseExtraction}>
            <motion.div style={modalLarge} initial={{opacity:0, scale:0.95}} animate={{opacity:1, scale:1}} exit={{opacity:0, scale:0.95}} onClick={e => e.stopPropagation()}>
              <div style={modalHeader}>
                <h2 style={{fontSize:'1.0625rem',fontWeight:700}}>AI 解析结果确认</h2>
                <span style={{fontSize:'.75rem',color:'#6b7280',flex:1}}>
                  解析了 {extractionData.file_results.length} 个文件
                  {extractionData.merge_result?.conflicts?.length ? `，${(extractionData.merge_result.conflicts as any[]).length} 处冲突` : ''}
                </span>
                <button onClick={onCloseExtraction} style={{background:'none',border:'none',color:'#a3a3a3',cursor:'pointer'}}><X size={20}/></button>
              </div>
              <div style={modalBody}>
                {SECTIONS.map(section => {
                  const filledCount = section.fields.filter(f => editing[f] != null && editing[f] !== '').length;
                  const hasConflict = extractionData.merge_result?.conflicts?.some((c: any) => section.fields.includes(c.field));
                  return (
                    <details key={section.key} style={sectionStyle}>
                      <summary style={summaryStyle}>
                        <span>{section.label} ({filledCount}/{section.fields.length})</span>
                        {hasConflict && <span style={{color:'#ef4444',fontSize:'.75rem'}}>⚠ 冲突</span>}
                      </summary>
                      <div style={gridStyle}>
                        {section.fields.map(field => {
                          const val = editing[field];
                          const isConflict = extractionData.merge_result?.conflicts?.some((c: any) => c.field === field);
                          const enums = section.enumFields[field];
                          return (
                            <div key={field} style={fieldStyle}>
                              <label style={labelStyle}>{FIELD_LABELS[field] || field}</label>
                              {enums ? (
                                <select value={val ?? ''} onChange={e => setEditing({...editing, [field]: e.target.value || null})} style={inputStyle(!!isConflict)}>
                                  <option value="">未提取到</option>
                                  {enums.map(e => <option key={e} value={e}>{e}</option>)}
                                </select>
                              ) : (
                                <input type="text" value={val ?? ''} onChange={e => setEditing({...editing, [field]: e.target.value || null})} placeholder="未提取到" style={inputStyle(!!isConflict)} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  );
                })}
              </div>
              <div style={modalFooter}>
                <button onClick={onCloseExtraction} style={{padding:'8px 20px',border:'1px solid #e8e2d8',borderRadius:8,background:'white',cursor:'pointer'}}>取消</button>
                <button onClick={() => onCloseExtraction()} style={{padding:'8px 20px',border:'none',borderRadius:8,background:'#f59e0b',color:'white',cursor:'pointer'}}>保存草稿</button>
                <button onClick={handleConfirm} disabled={confirming} style={{padding:'8px 20px',border:'none',borderRadius:8,background:confirming?'#c7d2fe':'#6366f1',color:'white',cursor:'pointer'}}>
                  {confirming ? '保存中...' : '确认保存'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 详情弹窗 */}
      <AnimatePresence>
        {showDetailModal && detailData && <DetailView detailData={detailData} onClose={onCloseDetail} />}
      </AnimatePresence>
    </>
  );
}

function DetailView({ detailData, onClose }: { detailData: QualityIncidentDetail; onClose: () => void }) {
  const sectionKeys = Object.keys(SECTION_LABELS) as Array<keyof typeof SECTION_LABELS>;
  // 默认全部折叠
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(
    () => Object.fromEntries(sectionKeys.map(k => [k, true]).concat([['_files', true]]))
  );
  const [onlyParsed, setOnlyParsed] = useState(false);

  const toggleSection = (key: string) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const allFields = sectionKeys.map(section => {
    const data = (detailData as any)[section];
    const all = data && typeof data === 'object'
      ? Object.entries(data)
      : [];
    const filled = all.filter(([,v]) => v != null && v !== '');
    return { section, fields: onlyParsed ? filled : all, filledCount: filled.length, totalCount: all.length };
  });

  return (
    <motion.div style={modalOverlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={onClose}>
      <motion.div style={{...modalLarge, maxWidth: 960, maxHeight:'92vh'}} initial={{opacity:0, scale:0.95}} animate={{opacity:1, scale:1}} exit={{opacity:0, scale:0.95}} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{padding:'16px 24px',borderBottom:'1px solid #e5e7eb',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <h2 style={{fontSize:'1.125rem',fontWeight:700,margin:0}}>{detailData.incident_name || detailData.incident_no}</h2>
            <p style={{fontSize:'.75rem',color:'#9ca3af',margin:'2px 0 0'}}>编号：{detailData.incident_no} · {detailData.files?.length || 0} 个关联文件</p>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:12,flexShrink:0}}>
            <label style={{
              display:'inline-flex',alignItems:'center',gap:6,fontSize:'.75rem',color: onlyParsed ? '#6366f1' : '#9ca3af',
              cursor:'pointer',userSelect:'none',whiteSpace:'nowrap',
              padding:'5px 12px',borderRadius:20,border:`1px solid ${onlyParsed ? '#c7d2fe' : '#e5e7eb'}`,
              background: onlyParsed ? '#eef2ff' : '#fff',transition:'all .2s',
            }}>
              <input type="checkbox" checked={onlyParsed} onChange={e => setOnlyParsed(e.target.checked)} style={{display:'none'}} />
              {onlyParsed ? '✓' : '☐'} 只看解析结果
            </label>
            <button onClick={onClose} style={{background:'none',border:'none',color:'#9ca3af',cursor:'pointer',padding:4,flexShrink:0}}><X size={22}/></button>
          </div>
        </div>

        {/* Body */}
        <div style={{padding:'12px 24px 24px',overflow:'auto',flex:1}}>
          {allFields.map(({ section, fields, filledCount, totalCount }) => {
            const isOpen = !collapsed[section];
            return (
              <div key={section} style={{marginBottom:8,border:'1px solid #e5e7eb',borderRadius:10,overflow:'hidden'}}>
                <div onClick={() => toggleSection(section)}
                  style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 16px',background:'#fafbfc',cursor:'pointer',userSelect:'none'}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <span style={{fontSize:10,color:'#9ca3af',transition:'transform .2s',transform: isOpen ? 'rotate(90deg)' : 'rotate(0)'}}>&#9654;</span>
                    <span style={{fontSize:'.875rem',fontWeight:600}}>{SECTION_LABELS[section]}</span>
                    {!onlyParsed ? (
                      <span style={{fontSize:'.6875rem',color: filledCount > 0 ? '#059669' : '#9ca3af',background: filledCount > 0 ? '#ecfdf5' : '#f3f4f6',borderRadius:10,padding:'1px 8px'}}>
                        {filledCount}/{totalCount}
                      </span>
                    ) : (
                      <span style={{fontSize:'.6875rem',color:'#059669',background:'#ecfdf5',borderRadius:10,padding:'1px 8px'}}>{filledCount} 项</span>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div style={{padding:'8px 16px 16px'}}>
                    {fields.length > 0 ? (
                      <div style={{display:'grid',gridTemplateColumns:'repeat(3, 1fr)',gap:'1px 16px'}}>
                        {fields.map(([k, v]) => {
                          const empty = v == null || v === '';
                          return (
                            <div key={k} style={{padding:'8px 0',borderBottom:'1px solid #f3f4f6',display:'flex',flexDirection:'column',gap:2,opacity: empty ? 0.5 : 1}}>
                              <span style={{fontSize:'.6875rem',color:'#9ca3af',fontWeight:500}}>{FIELD_LABELS[k] || k}</span>
                              <span style={{fontSize:'.8125rem',color: empty ? '#d1d5db' : '#1f2937',wordBreak:'break-word'}}>
                                {empty ? '—' : formatFieldValue(k, v)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p style={{textAlign:'center',color:'#d1d5db',fontSize:'.8125rem',margin:0}}>暂无数据</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* 关联文件 */}
          {detailData.files && detailData.files.length > 0 && (
            <div style={{border:'1px solid #e5e7eb',borderRadius:10,overflow:'hidden'}}>
              <div onClick={() => toggleSection('_files')}
                style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 16px',background:'#fafbfc',cursor:'pointer',userSelect:'none'}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontSize:10,color:'#9ca3af',transition:'transform .2s',transform: collapsed['_files'] ? 'rotate(0)' : 'rotate(90deg)'}}>&#9654;</span>
                  <span style={{fontSize:'.875rem',fontWeight:600}}>关联文件</span>
                  <span style={{fontSize:'.6875rem',color:'#9ca3af',background:'#f3f4f6',borderRadius:10,padding:'1px 8px'}}>{detailData.files.length}</span>
                </div>
              </div>
              {!collapsed['_files'] && (
                <div style={{padding:'8px 16px 16px'}}>
                  {detailData.files.map(f => (
                    <div key={f.file_id} style={{fontSize:'.8125rem',padding:'6px 0',borderBottom:'1px solid #f3f4f6',display:'flex',justifyContent:'space-between'}}>
                      <span>{f.file_name}</span>
                      <span style={{color:'#9ca3af'}}>{f.file_type?.toUpperCase()} · {formatSize(f.file_size)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{padding:'12px 24px',borderTop:'1px solid #e5e7eb',display:'flex',justifyContent:'flex-end'}}>
          <button onClick={onClose} style={{padding:'8px 24px',border:'1px solid #d1d5db',borderRadius:8,background:'white',cursor:'pointer',fontSize:'.875rem'}}>关闭</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function formatFieldValue(key: string, val: any): string {
  const dateFields = ['discovery_time','occurrence_time','closure_time','planned_completion','actual_completion'];
  if (dateFields.includes(key) && val) {
    try {
      return new Date(val).toLocaleDateString('zh-CN', {year:'numeric',month:'long',day:'numeric'});
    } catch {}
  }
  return String(val);
}

function formatSize(s?: number | null) {
  if (!s) return '-';
  return s < 1024 * 1024 ? `${(s / 1024).toFixed(1)} KB` : `${(s / (1024 * 1024)).toFixed(1)} MB`;
}
